import { generateSnakeColors, getPlayerColorKey, PLAYER_COLOR_VARIANT_COUNT, toCssHex } from '../../game/snakeColors'

/**
 * Animated snake preview migrated from MainMenuScreen for SNAKE-30. Lives in
 * the Profile "Скин" section now — tap to cycle palettes is preserved.
 */

const COLOR_SEED_KEY = 'star-snake-color-seed'
const PREVIEW_STYLE_ID = 'star-snake-snake-preview-style'

export function loadColorSeed(): number {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(COLOR_SEED_KEY) : null
  if (stored !== null) {
    const val = parseInt(stored, 10)
    if (!isNaN(val) && val >= 0) return val % PLAYER_COLOR_VARIANT_COUNT
  }
  return 0
}

function saveColorSeed(seed: number): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(COLOR_SEED_KEY, String(seed))
}

function ensurePreviewStyle(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(PREVIEW_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = PREVIEW_STYLE_ID
  style.textContent = `
    @keyframes profile-snake-wave { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
    .profile-snake-seg { animation: profile-snake-wave 2s ease-in-out infinite; border-radius: 50%; flex-shrink: 0; }
  `
  document.head.appendChild(style)
}

export function mountSnakePreview(container: HTMLElement): void {
  ensurePreviewStyle()

  let seed = loadColorSeed()

  const render = (): void => {
    while (container.firstChild) container.removeChild(container.firstChild)

    const colors = generateSnakeColors(getPlayerColorKey(seed))
    const color1 = toCssHex(colors.body[0])
    const color2 = toCssHex(colors.body[1])
    const headColor = toCssHex(colors.head)
    const palette = [color1, color2]

    const row = document.createElement('div')
    row.style.cssText = 'display:flex; align-items:center; margin-left:-6px;'

    const segDefs: Array<{ size: number; colorIdx: 0 | 1; opacity: number; delay: number; glow: boolean }> = [
      { size: 6, colorIdx: 0, opacity: 0.4, delay: 1.2, glow: false },
      { size: 8, colorIdx: 0, opacity: 0.5, delay: 1.15, glow: false },
      { size: 8, colorIdx: 1, opacity: 0.6, delay: 1.1, glow: false },
      { size: 10, colorIdx: 0, opacity: 0.7, delay: 1.05, glow: false },
      { size: 10, colorIdx: 1, opacity: 0.8, delay: 1.0, glow: false },
    ]
    for (let i = 0; i < 10; i++) {
      segDefs.push({ size: 12, colorIdx: (i % 2) as 0 | 1, opacity: 1, delay: 0.95 - i * 0.05, glow: true })
    }
    for (let i = 0; i < 9; i++) {
      segDefs.push({ size: 14, colorIdx: (i % 2) as 0 | 1, opacity: 1, delay: 0.45 - i * 0.05, glow: true })
    }

    for (const seg of segDefs) {
      const el = document.createElement('div')
      const c = palette[seg.colorIdx]
      el.className = 'profile-snake-seg'
      el.style.cssText = `
        width: ${seg.size}px; height: ${seg.size}px;
        background: ${c}; opacity: ${seg.opacity};
        animation-delay: ${seg.delay}s;
        margin-left: -6px;
        ${seg.glow ? `box-shadow: 0 0 12px ${c}80;` : ''}
      `
      row.appendChild(el)
    }

    const head = document.createElement('div')
    head.className = 'profile-snake-seg'
    head.style.cssText = `
      width: 20px; height: 20px; background: ${headColor};
      border: 2px solid rgba(255,255,255,0.4);
      animation-delay: 0s; margin-left: -6px; position: relative;
      display: flex; align-items: center; justify-content: center;
      z-index: 50; box-shadow: 0 0 12px ${headColor}80;
    `
    const eyes = document.createElement('div')
    eyes.style.cssText = 'display:flex; gap:4px; margin-left:4px;'
    for (let i = 0; i < 2; i++) {
      const eye = document.createElement('div')
      eye.style.cssText = 'width:6px; height:6px; background:white; border-radius:50%; display:flex; align-items:center; justify-content:center;'
      const pupil = document.createElement('div')
      pupil.style.cssText = 'width:2px; height:2px; background:black; border-radius:50%;'
      eye.appendChild(pupil)
      eyes.appendChild(eye)
    }
    head.appendChild(eyes)
    row.appendChild(head)

    container.appendChild(row)
  }

  render()

  container.style.cursor = 'pointer'
  container.addEventListener('click', () => {
    seed = (seed + 1) % PLAYER_COLOR_VARIANT_COUNT
    saveColorSeed(seed)
    render()
  })
}
